import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { ScrollToTop } from "@/components/ScrollToTop";
import Index from "./pages/Index";
import { BrandedLoader } from "@/components/BrandedLoader";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Lazy load non-critical routes
const Auth = lazy(() => import("./pages/Auth"));
const Sonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster }))
);
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CreateFundraiser = lazy(() => import("./pages/CreateFundraiser"));
const ExploreCampaigns = lazy(() => import("./pages/ExploreCampaigns"));
const CampaignDetail = lazy(() => import("./pages/CampaignDetail"));
const Organizations = lazy(() => import("./pages/Organizations"));
const Stories = lazy(() => import("./pages/Stories"));
const StoryDetail = lazy(() => import("./pages/StoryDetail"));
const About = lazy(() => import("./pages/About"));
const ViewPage = lazy(() => import("./pages/ViewPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const PageLoader = () => <BrandedLoader />;

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) return <PageLoader />;
  if (!session) return <Navigate to="/auth" replace />;
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Suspense fallback={null}>
        <Sonner />
      </Suspense>
      <BrowserRouter>
        <ScrollToTop />
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/explore" element={<ExploreCampaigns />} />
            <Route path="/organizations" element={<Organizations />} />
            <Route path="/stories" element={<Stories />} />
            <Route path="/stories/:slug" element={<StoryDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/page/:slug" element={<ViewPage />} />
            <Route path="/campaign/:id" element={<CampaignDetail />} />
            <Route
              path="/create-fundraiser" 
              element={
                <ProtectedRoute>
                  <CreateFundraiser />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
